import React, { createContext, useContext, useState, useEffect } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface PresenceContextType {
  onlineUserIds: string[];
}

const PresenceContext = createContext<PresenceContextType>({ onlineUserIds: [] });

export const usePresence = () => useContext(PresenceContext);

const IDLE_MS = 30 * 60 * 1000; // 30 Minuten ohne Eingabe
const HIDDEN_GRACE_MS = 60 * 1000; // 60 Sekunden Karenz im Hintergrund
const ACTIVITY_THROTTLE_MS = 30 * 1000;

const PRESENCE_TOPIC = 'online-users';

// supabase-js haelt pro Topic genau einen Channel (RealtimeClient.channel) und
// entfernt ihn erst, wenn der Server das Leave bestaetigt hat
// (RealtimeChannel._onClose -> RealtimeClient._remove). Startet der Effekt
// vorher erneut, liefert supabase.channel(PRESENCE_TOPIC) den alten, noch
// subscribten Channel zurueck - und .on('presence', ...) wirft dann
// "cannot add `presence` callbacks ... after `subscribe()`".
// Auf- und Abbau werden deshalb global serialisiert.
let presenceLifecycle: Promise<void> = Promise.resolve();

const queuePresenceStep = (step: () => void | Promise<void>) => {
  presenceLifecycle = presenceLifecycle.then(step).catch(() => {
    // Ein fehlgeschlagener Schritt darf die Kette nicht dauerhaft blockieren.
  });
};

export const PresenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // Nur die User-ID als Dependency: das user-Objekt bekommt bei jedem
  // Auth-Event (TOKEN_REFRESHED, INITIAL_SESSION, getSession) eine neue
  // Objekt-Identitaet, obwohl sich die Anmeldung nicht geaendert hat.
  const userId = user?.id;
  const [onlineUserIds, setOnlineUserIds] = useState<string[]>([]);

  useEffect(() => {
    if (!userId) {
      setOnlineUserIds([]);
      return;
    }

    let channel: RealtimeChannel | null = null;
    let subscribed = false;
    let tracked = false;
    let lastActivity = Date.now();
    let lastActivityHandled = 0;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const shouldBeTracked = () =>
      document.visibilityState === 'visible' && Date.now() - lastActivity < IDLE_MS;

    const doTrack = async () => {
      if (disposed || !channel || !subscribed || tracked) return;
      tracked = true;
      await channel.track({ user_id: userId, online_at: new Date().toISOString() });
    };

    const doUntrack = async () => {
      if (!channel || !subscribed || !tracked) return;
      tracked = false;
      await channel.untrack();
    };

    const sync = () => {
      if (shouldBeTracked()) void doTrack();
      else void doUntrack();
    };

    const scheduleIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        void doUntrack();
      }, IDLE_MS);
    };

    const onActivity = () => {
      const now = Date.now();
      lastActivity = now;
      if (now - lastActivityHandled < ACTIVITY_THROTTLE_MS) return;
      lastActivityHandled = now;
      scheduleIdle();
      sync();
    };

    const onVisibility = () => {
      if (hiddenTimer) {
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
      }
      if (document.visibilityState === 'visible') {
        lastActivity = Date.now();
        lastActivityHandled = lastActivity;
        scheduleIdle();
        sync();
      } else {
        hiddenTimer = setTimeout(() => {
          void doUntrack();
        }, HIDDEN_GRACE_MS);
      }
    };

    const events: Array<keyof DocumentEventMap> = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(ev => document.addEventListener(ev, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    scheduleIdle();

    queuePresenceStep(() => {
      if (disposed) return;
      const ch = supabase.channel(PRESENCE_TOPIC, {
        config: { presence: { key: userId } },
      });
      channel = ch;

      ch
        .on('presence', { event: 'sync' }, () => {
          const state = ch.presenceState();
          setOnlineUserIds(Object.keys(state));
        })
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            subscribed = true;
            sync();
          }
        });
    });

    return () => {
      disposed = true;
      if (idleTimer) clearTimeout(idleTimer);
      if (hiddenTimer) clearTimeout(hiddenTimer);
      events.forEach(ev => document.removeEventListener(ev, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);

      queuePresenceStep(async () => {
        const ch = channel;
        channel = null;
        subscribed = false;
        tracked = false;
        if (!ch) return;
        try {
          await ch.untrack();
        } catch {
          // Untrack ist best effort - das Leave entfernt die Presence ohnehin.
        }
        // Erst wenn removeChannel aufgeloest ist, hat supabase-js den Channel
        // aus seiner Registry entfernt. Danach darf der naechste Lauf starten.
        await supabase.removeChannel(ch);
      });
    };
  }, [userId]);

  return (
    <PresenceContext.Provider value={{ onlineUserIds }}>
      {children}
    </PresenceContext.Provider>
  );
};
